import { useTranslation } from 'react-i18next';

import { formatDayOffset } from '~/utils/trialPricing';

import styles from './TrialTimeline.module.css';

/* ---------- icons (stroke, 24px grid — same style as AuthKit) ---------- */
function IconCheck() {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="M20 6 9 17l-5-5" />
        </svg>
    );
}
function IconBell() {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
            <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
        </svg>
    );
}
function IconCard() {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <rect x="2" y="5" width="20" height="14" rx="2" />
            <path d="M2 10h20" />
        </svg>
    );
}
function IconShield() {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
    );
}

export type TrialTimelineProps = {
    /** Prix mensuel affiché (ex. « 49 € », « $59 », « 39 € » avec coupon). */
    priceLabel: string;
    /** Prix barré quand un coupon partenaire s'applique. */
    strikeLabel?: string;
    /** Mention partenaire (ex. « Offre réseau V&B »). */
    tag?: string;
    /** Périodicité de la formule choisie (mensuel par défaut). */
    period?: 'month' | 'year';
    /** Durée de l'essai (30 jours pour une inscription parrainée, utils/referral). */
    trialDays?: number;
};

/**
 * Frise datée de l'essai gratuit, affichée juste avant le passage sur Stripe :
 * aujourd'hui (accès complet) → J+7 (email de rappel, avec lien d'annulation)
 * → J+14 (premier prélèvement). Une seule promesse, alignée sur ce que Stripe
 * fait réellement (rappel envoyé 7 jours avant la fin de l'essai) : « on vous
 * prévient, et vous pouvez annuler en ligne ». Objectif : désamorcer
 * l'annulation-réflexe du jour 0 (28 % des annulations en trial).
 *
 * Les dates sont indicatives (calculées à l'affichage) : Stripe fixe la vraie
 * fin d'essai à la validation du paiement.
 */
export function TrialTimeline({ priceLabel, strikeLabel, tag, period = 'month', trialDays = 14 }: TrialTimelineProps) {
    const { t, i18n } = useTranslation();
    const lang = i18n.language;

    const today = formatDayOffset(0, lang);
    const reminderDate = formatDayOffset(trialDays - 7, lang);
    const startDate = formatDayOffset(trialDays, lang);

    return (
        <div className={styles.card}>
            <ol className={styles.steps}>
                <li className={styles.step}>
                    <div className={styles.iconCol}>
                        <span className={`${styles.icon} ${styles.iconDone}`}>
                            <IconCheck />
                        </span>
                        <span className={styles.line} />
                    </div>
                    <div className={styles.text}>
                        <span className={`${styles.date} ${styles.dateDone}`}>
                            {t('global.infos.trial.today')} · {today}
                        </span>
                        <span className={styles.title}>{t('global.infos.trial.todayTitle')}</span>
                    </div>
                </li>

                <li className={styles.step}>
                    <div className={styles.iconCol}>
                        <span className={`${styles.icon} ${styles.iconNext}`}>
                            <IconBell />
                        </span>
                        <span className={styles.line} />
                    </div>
                    <div className={styles.text}>
                        <span className={`${styles.date} ${styles.dateNext}`}>{reminderDate}</span>
                        <span className={styles.title}>{t('global.infos.trial.reminderTitle')}</span>
                        <span className={styles.desc}>{t('global.infos.trial.reminderDesc')}</span>
                    </div>
                </li>

                <li className={styles.step}>
                    <div className={styles.iconCol}>
                        <span className={`${styles.icon} ${styles.iconLater}`}>
                            <IconCard />
                        </span>
                    </div>
                    <div className={styles.text}>
                        <span className={styles.date}>{startDate}</span>
                        <span className={styles.title}>
                            {t(
                                period === 'year'
                                    ? 'global.infos.trial.startTitleYear'
                                    : 'global.infos.trial.startTitle',
                                {
                                    price: priceLabel,
                                }
                            )}
                            {strikeLabel && <s className={styles.strike}>{strikeLabel}</s>}
                        </span>
                        {tag && <span className={styles.tag}>{tag}</span>}
                        <span className={styles.desc}>{t('global.infos.trial.startDesc')}</span>
                    </div>
                </li>
            </ol>

            <div className={styles.note}>
                <span className={styles.noteIcon}>
                    <IconShield />
                </span>
                <span>{t('global.infos.trial.cancelNote', { date: startDate })}</span>
            </div>
        </div>
    );
}
